/**
 * Heightfield splat accumulation — CPU reference for the GPU additive pass.
 *
 * Heightfield layout (RGBA16F):
 *   R = GRANULAR deposit height (m) — grains, grit, chunk/chip recycle stamps
 *   G = height-weighted material mix vs TOTAL visual height; mix = G / (R+A)
 *   B = freshness (1 on deposit; unused by the ground shader so idle frames
 *       stay byte-stable even if B is left alone)
 *   A = FILM deposit height (m) — dying fines only. Thin coating; never
 *       participates in repose or speckle diffusion.
 *
 * Visual / collision / pileVolume / ground displacement = R + A.
 * Repose + speckle operate on R only; A is copied through unchanged.
 *
 * Base elevation (box-top Y, else 0) lives in a second static DataTexture,
 * not in A — A is the film channel. Reset/splat/compose add RGBA.
 *
 * Each dying grain stamps a soft, mass-normalized Gaussian kernel (~2 texel
 * radius via GL_POINTS) so isolated landings cannot form one-texel pillows.
 */

export interface HeightCell {
  height: number;
  materialWeighted: number;
  freshness: number;
}

export function emptyHeightCell(): HeightCell {
  return { height: 0, materialWeighted: 0, freshness: 0 };
}

export function cellMaterialMix(cell: HeightCell): number {
  return cell.height > 1e-8 ? cell.materialWeighted / cell.height : 0;
}

/**
 * One grain's volume spread as height, scaled for a readable pile.
 * `visualGain` is the same constant the splat shader uses.
 */
export function grainSplatAmount(radius: number, cellArea: number, visualGain: number): number {
  const r = Math.max(0, radius);
  const area = Math.max(1e-8, cellArea);
  const volume = (4 / 3) * Math.PI * r * r * r;
  return (volume / area) * visualGain;
}

/** Additive, mass-conserving mix of an incoming splat into a cell. */
export function accumulateSplat(cell: HeightCell, amount: number, materialId: number): HeightCell {
  const add = Math.max(0, amount);
  return {
    height: cell.height + add,
    materialWeighted: cell.materialWeighted + add * materialId,
    freshness: 1,
  };
}

/**
 * GL_POINTS diameter in heightfield texels. Fragment coverage ≈ pointSize²;
 * with the Gaussian below this yields a ~1.5–2 texel soft radius.
 */
export const SPLAT_POINT_SIZE = 5;

/**
 * Gaussian sharpness for p in point-coord units (gl_PointCoord − 0.5 ∈ [−0.5, 0.5]):
 *   w = exp(−|p|² · SHARPNESS)
 */
export const SPLAT_KERNEL_SHARPNESS = 8;

/** Wide, low-amplitude kernel for dying fines (dust-ring apron). */
export const FINE_SPLAT_POINT_SIZE = 17;

/** Flatter Gaussian so the apron is a veil, not a blob. */
export const FINE_SPLAT_KERNEL_SHARPNESS = 5;

/**
 * Visual scale for fine deposits. Grain piles use gain 6 on sphere volume;
 * fines deposit *mass units* so this must stay small or the ring becomes a
 * mesa.
 *
 * 0.28 stacked a max brick wall burst to ~25 mm at the wall base — past the
 * ground film threshold, so the apron painted as full pile tint. 0.07 keeps
 * the same near-epicenter footprint in the ~5–8 mm dusting band; distance
 * falloff + haze-skip then thin the far halo further.
 *
 * Note: heightfield R is *visual* displacement height. `getStats().pileVolume`
 * = ΣR × cellArea tracks that visual volume, not the honest fine-mass ledger
 * on spawn records — distant/haze fines intentionally contribute less (or
 * zero) visual height while their depositMass stays on the particle.
 */
export const FINE_SPLAT_VISUAL_GAIN = 0.07;

/**
 * Chunk → heightfield stamps. Gain 0.025 puts one lying brick at ~1–3 cm over
 * the flat disk (O(chunk thickness)); 0.28 stacked recycled stamps back into
 * the 0.5 m cone. Ledger mass is unchanged — this is visual height only.
 * The kernel (not just the gain) stops a recycle from becoming a point spike.
 */
export const CHUNK_SPLAT_VISUAL_GAIN = 0.025;
/**
 * Desktop 512² / 12 m → ~2.34 cm/texel. 13 texels ≈ 0.30 m disk, a bit
 * larger than the 0.19 m long axis so the lying brick is a pancake, not a
 * needle. Sharpness 0.85 keeps the interior almost flat (centre/edge ≈ 1.2).
 */
export const CHUNK_SPLAT_POINT_SIZE = 13;
export const CHUNK_SPLAT_KERNEL_SHARPNESS = 0.85;

/** Full visual height within this XZ distance of the burst epicenter (m). */
export const FINE_SPLAT_NEAR_M = 1.2;
/** Visual height fades to FINE_SPLAT_FAR_GAIN by this XZ distance (m). */
export const FINE_SPLAT_FAR_M = 3.0;
/** Visual height multiplier at/beyond FINE_SPLAT_FAR_M. */
export const FINE_SPLAT_FAR_GAIN = 0.2;
/**
 * Linger ≥ this marks the haze subset (micron-scale, no visible deposit).
 * Matches cloudShaders `step(4.5, linger)`.
 */
export const FINE_HAZE_LINGER_MIN = 4.5;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / Math.max(edge1 - edge0, 1e-12)));
  return t * t * (3 - 2 * t);
}

/** Distance falloff on fine *visual* height (honest depositMass unchanged). */
export function fineVisualDistanceGain(distXZ: number): number {
  const d = Math.max(0, distXZ);
  if (d <= FINE_SPLAT_NEAR_M) return 1;
  if (d >= FINE_SPLAT_FAR_M) return FINE_SPLAT_FAR_GAIN;
  return 1 + (FINE_SPLAT_FAR_GAIN - 1) * smoothstep(FINE_SPLAT_NEAR_M, FINE_SPLAT_FAR_M, d);
}

export function fineIsHaze(linger: number): boolean {
  return linger >= FINE_HAZE_LINGER_MIN;
}

/**
 * Visual splat amount for a dying fine. Haze → 0; otherwise depositMass ×
 * distance gain. Grain stamps never call this.
 */
export function fineVisualAmount(depositMass: number, distXZ: number, linger: number): number {
  if (!(depositMass > 0) || fineIsHaze(linger)) return 0;
  return depositMass * fineVisualDistanceGain(distXZ);
}

/** Discard weights below this (matches GPU early-out). */
export const SPLAT_KERNEL_MIN_WEIGHT = 0.02;

/**
 * Pile visual scale. Old path: gain 16 × unnormalized multi-texel kernel
 * (≈9× mass inflation) + 6 cm fragment cap → 1.5 m+ volcanoes and square
 * speckles. Mass-normalized kernel removes the inflation; gain 6 is a further
 * cut on the constant so a 5-burst concrete pile reads ~0.3–0.5 m (judged on
 * capture), not a mountain.
 */
export const SPLAT_VISUAL_GAIN = 6;

/**
 * Brick grit visual deposit. Multiplies sphere-volume stamps; ledger mass
 * is unchanged. Must match BRICK_SIGNATURE.grainDepositScale and the GLSL
 * literals in SPLAT_VERT. 0.10 still reposed five wall bursts into a 0.3 m
 * grit cone once film was excluded; 0.07 keeps a low talus and still
 * clears the wall-base deposit e2e floor.
 */
export const GRAIN_DEPOSIT_SCALE_BRICK = 0.07;
/** Must match BRICK_SIGNATURE.gritSizeScale. Splat restores this so 0.35× is the only visual cut. */
export const GRIT_SIZE_SCALE_BRICK = 0.14;
/** Full visual height within this XZ distance of the burst epicenter (m). */
export const GRAIN_SPLAT_NEAR_M = 1.6;
/** Visual height fades to GRAIN_SPLAT_FAR_GAIN by this XZ distance (m). */
export const GRAIN_SPLAT_FAR_M = 4.0;
/** Visual height multiplier at/beyond GRAIN_SPLAT_FAR_M (gentler than fines). */
export const GRAIN_SPLAT_FAR_GAIN = 0.45;
/** Wide kernel so first-contact grit deaths form a mound, not a poured cone. */
export const GRIT_SPLAT_POINT_SIZE = 13;
export const GRIT_SPLAT_KERNEL_SHARPNESS = 5;

/** Distance falloff on brick-grit *visual* height (honest grain mass unchanged). */
export function grainVisualDistanceGain(distXZ: number): number {
  const d = Math.max(0, distXZ);
  if (d <= GRAIN_SPLAT_NEAR_M) return 1;
  if (d >= GRAIN_SPLAT_FAR_M) return GRAIN_SPLAT_FAR_GAIN;
  return 1 + (GRAIN_SPLAT_FAR_GAIN - 1) * smoothstep(GRAIN_SPLAT_NEAR_M, GRAIN_SPLAT_FAR_M, d);
}

/**
 * Visual splat amount for a dying grain. `depositScale === 1` is the
 * concrete/stone path (no extra scale, no distance bias). Brick grit
 * (`depositScale < 1`) applies the scale and the crater-vicinity curve.
 */
export function grainVisualAmount(volume: number, distXZ: number, depositScale: number): number {
  if (!(volume > 0)) return 0;
  if (depositScale >= 0.999) return volume;
  return volume * depositScale * grainVisualDistanceGain(distXZ);
}

/**
 * Weight at a pixel offset (dx, dy) from the splat centre, using the same
 * mapping as GL_POINTS: p = offset / pointSize.
 */
export function splatKernelWeight(
  dx: number,
  dy: number,
  pointSize: number = SPLAT_POINT_SIZE,
  sharpness: number = SPLAT_KERNEL_SHARPNESS,
): number {
  const inv = 1 / Math.max(pointSize, 1e-6);
  const px = dx * inv;
  const py = dy * inv;
  return Math.exp(-(px * px + py * py) * sharpness);
}

/**
 * Sum of kernel weights over the pointSize×pointSize pixel grid (pixel
 * centres). GPU multiplies each fragment by `1 / this` so Σh == amount.
 */
export function splatKernelWeightSum(
  pointSize: number = SPLAT_POINT_SIZE,
  sharpness: number = SPLAT_KERNEL_SHARPNESS,
): number {
  const n = Math.max(1, Math.round(pointSize));
  let sum = 0;
  // Pixel centres relative to point centre: …, -2, -1, 0, 1, 2 for n=5.
  const half = (n - 1) / 2;
  for (let j = 0; j < n; j += 1) {
    for (let i = 0; i < n; i += 1) {
      const w = splatKernelWeight(i - half, j - half, n, sharpness);
      if (w >= SPLAT_KERNEL_MIN_WEIGHT) sum += w;
    }
  }
  return sum > 1e-12 ? sum : 1;
}

/** Cached GPU norm (1 / Σw) for the default point size. */
export const SPLAT_KERNEL_INV_SUM = 1 / splatKernelWeightSum(SPLAT_POINT_SIZE);

/** Cached GPU norm for the wide fine kernel. */
export const FINE_SPLAT_KERNEL_INV_SUM =
  1 / splatKernelWeightSum(FINE_SPLAT_POINT_SIZE, FINE_SPLAT_KERNEL_SHARPNESS);

/** Cached GPU norm for the wide brick-grit kernel. */
export const GRIT_SPLAT_KERNEL_INV_SUM =
  1 / splatKernelWeightSum(GRIT_SPLAT_POINT_SIZE, GRIT_SPLAT_KERNEL_SHARPNESS);

/** Cached GPU norm for the flat chunk-recycle disk. */
export const CHUNK_SPLAT_KERNEL_INV_SUM =
  1 / splatKernelWeightSum(CHUNK_SPLAT_POINT_SIZE, CHUNK_SPLAT_KERNEL_SHARPNESS);

/** Visual / collision height = granular (R) + film (A). */
export function depositHeight(granular: number, film = 0): number {
  const g = Number.isFinite(granular) ? Math.max(0, granular) : 0;
  const f = Number.isFinite(film) ? Math.max(0, film) : 0;
  return g + f;
}

/**
 * Stamp a grain onto a discrete height grid (CPU analogue of the GL_POINTS splat).
 * Kernel is mass-normalized: total height added equals `amount` (within float error).
 * Returns the total height added (for conservation checks).
 */
export function stampGrain(
  heights: Float32Array,
  weighted: Float32Array,
  size: number,
  cx: number,
  cy: number,
  amount: number,
  materialId: number,
  pointSize: number = SPLAT_POINT_SIZE,
  sharpness: number = SPLAT_KERNEL_SHARPNESS,
): number {
  if (amount <= 0) return 0;
  const n = Math.max(1, Math.round(pointSize));
  const half = (n - 1) / 2;
  const r = Math.floor(n / 2);

  let totalW = 0;
  for (let dy = -r; dy <= r; dy += 1) {
    for (let dx = -r; dx <= r; dx += 1) {
      const w = splatKernelWeight(dx, dy, n, sharpness);
      if (w < SPLAT_KERNEL_MIN_WEIGHT) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= size || y >= size) continue;
      totalW += w;
    }
  }
  if (totalW < 1e-12) return 0;

  let added = 0;
  for (let dy = -r; dy <= r; dy += 1) {
    for (let dx = -r; dx <= r; dx += 1) {
      const w = splatKernelWeight(dx, dy, n, sharpness);
      if (w < SPLAT_KERNEL_MIN_WEIGHT) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= size || y >= size) continue;
      const i = y * size + x;
      const add = (amount * w) / totalW;
      heights[i] += add;
      weighted[i] += add * materialId;
      added += add;
    }
  }
  // half is referenced so the grid stays centred the same way as weightSum.
  void half;
  return added;
}

/** Wide-kernel stamp for a dying fine. Conserves `amount` the same way as grains. */
export function stampFine(
  heights: Float32Array,
  weighted: Float32Array,
  size: number,
  cx: number,
  cy: number,
  amount: number,
  materialId: number,
): number {
  return stampGrain(
    heights,
    weighted,
    size,
    cx,
    cy,
    amount,
    materialId,
    FINE_SPLAT_POINT_SIZE,
    FINE_SPLAT_KERNEL_SHARPNESS,
  );
}

/**
 * Recycled-chunk stamp: same mass-normalized kernel as grains, but the wide
 * flat disk so one brick's volume lands as a ~chunk-thick pancake, not a
 * spike. Writes the GRANULAR channel (caller passes the R array).
 */
export function stampChunk(
  heights: Float32Array,
  weighted: Float32Array,
  size: number,
  cx: number,
  cy: number,
  amount: number,
  materialId: number,
): number {
  return stampGrain(
    heights,
    weighted,
    size,
    cx,
    cy,
    amount,
    materialId,
    CHUNK_SPLAT_POINT_SIZE,
    CHUNK_SPLAT_KERNEL_SHARPNESS,
  );
}
